import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  FileText,
  Plus,
  Eye,
  Search,
  Layers,
  X,
  Check,
  Trash2,
  Copy,
  ToggleLeft,
  ToggleRight,
  Filter,
} from 'lucide-react';
import axiosClient from '../api/axiosClient';
import toast from 'react-hot-toast';

export default function PageManager() {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [newPageData, setNewPageData] = useState({
    title: '',
    slug: '',
    description: '',
    seoTitle: '',
    metaDescription: '',
    keywords: '',
  });

  useEffect(() => {
    fetchPages();
  }, []);

  const fetchPages = async () => {
    try {
      const res = await axiosClient.get('/pages');
      setPages(res.data);
    } catch (err) {
      toast.error('Failed to fetch pages.');
    } finally {
      setLoading(false);
    }
  };

  const handleTitleChange = (val) => {
    setNewPageData(prev => ({
      ...prev,
      title: val,
      slug: val.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, ''),
      seoTitle: `${val} | FAJTech Managed IT`,
    }));
  };

  const handleCreatePage = async (e) => {
    e.preventDefault();
    if (!newPageData.title || !newPageData.slug) {
      toast.error('Page Title and Slug are required.');
      return;
    }

    try {
      await axiosClient.post('/pages', newPageData);
      toast.success('Page created successfully!');
      setCreateModalOpen(false);
      setNewPageData({
        title: '',
        slug: '',
        description: '',
        seoTitle: '',
        metaDescription: '',
        keywords: '',
      });
      fetchPages();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to create page.');
    }
  };

  const handleDeletePage = async (page) => {
    if (window.confirm(`Are you sure you want to delete "${page.title}"? This will also delete all sections.`)) {
      try {
        await axiosClient.delete(`/pages/${page.id}`);
        toast.success('Page deleted successfully.');
        fetchPages();
      } catch (err) {
        toast.error('Failed to delete page.');
      }
    }
  };

  const handleDuplicatePage = async (page) => {
    try {
      await axiosClient.post(`/pages/${page.id}/duplicate`);
      toast.success(`Page "${page.title}" duplicated!`);
      fetchPages();
    } catch (err) {
      toast.error('Failed to duplicate page.');
    }
  };

  const handleToggleStatus = async (page) => {
    const newStatus = page.status === 'PUBLISHED' ? 'DRAFT' : 'PUBLISHED';
    try {
      await axiosClient.put(`/pages/${page.id}`, { status: newStatus });
      toast.success(`Page ${newStatus === 'PUBLISHED' ? 'published' : 'unpublished'}.`);
      fetchPages();
    } catch (err) {
      toast.error('Failed to update page status.');
    }
  };

  const filteredPages = pages.filter(p => {
    const matchesSearch =
      p.title.toLowerCase().includes(search.toLowerCase()) ||
      p.slug.toLowerCase().includes(search.toLowerCase());
    const matchesStatus = statusFilter === 'ALL' || p.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const publishedCount = pages.filter(p => p.status === 'PUBLISHED').length;
  const draftCount = pages.filter(p => p.status !== 'PUBLISHED').length;

  return (
    <div className="space-y-6 animate-in fade-in duration-300">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-black text-white tracking-tight flex items-center space-x-2">
            <FileText className="text-blue-500" />
            <span>Website Pages & Page Builder</span>
          </h1>
          <p className="text-xs text-slate-400 mt-1">
            Manage static pages, SEO metadata, and drag-and-drop section layouts
          </p>
        </div>

        <div className="flex items-center space-x-3">
          <button
            onClick={() => setCreateModalOpen(true)}
            className="px-4 py-2.5 rounded-xl font-bold text-xs bg-blue-600 hover:bg-blue-500 text-white shadow-lg shadow-blue-600/30 flex items-center space-x-2 transition-all"
          >
            <Plus size={16} />
            <span>Create New Page</span>
          </button>
        </div>
      </div>

      {/* Filter and Search */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-4 bg-slate-900/60 p-4 rounded-2xl border border-slate-800">
        <div className="flex items-center space-x-3 w-full sm:w-auto">
          <div className="relative flex-1 sm:w-80">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={15} />
            <input
              type="text"
              placeholder="Search pages by title, slug..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2 rounded-xl border border-slate-800 bg-slate-950 text-white text-xs placeholder-slate-500 focus:outline-none focus:border-blue-500"
            />
          </div>

          {/* Status filter pills */}
          <div className="hidden sm:flex items-center space-x-1.5">
            <Filter size={14} className="text-slate-500" />
            {[
              { key: 'ALL', label: 'All' },
              { key: 'PUBLISHED', label: `Published (${publishedCount})` },
              { key: 'DRAFT', label: `Draft (${draftCount})` },
            ].map(f => (
              <button
                key={f.key}
                onClick={() => setStatusFilter(f.key)}
                className={`px-3 py-1.5 rounded-lg text-[10px] font-bold transition-all whitespace-nowrap ${
                  statusFilter === f.key
                    ? 'bg-blue-600 text-white shadow-md'
                    : 'bg-slate-800 text-slate-400 hover:text-white'
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        <div className="text-xs text-slate-400 font-medium">
          Showing <span className="text-white font-bold">{filteredPages.length}</span> of {pages.length} Pages
        </div>
      </div>

      {/* Pages Table */}
      <div className="bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden shadow-xl">
        {loading ? (
          <div className="flex items-center justify-center h-48">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
          </div>
        ) : filteredPages.length === 0 ? (
          <div className="text-center py-16 text-slate-400 text-xs">
            No pages found matching your search.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs text-slate-300">
              <thead className="bg-slate-950 text-slate-400 uppercase font-bold text-[10px] tracking-wider border-b border-slate-800">
                <tr>
                  <th className="px-6 py-4">Page Title</th>
                  <th className="px-6 py-4">Slug Route</th>
                  <th className="px-6 py-4">Status</th>
                  <th className="px-6 py-4">Sections</th>
                  <th className="px-6 py-4">SEO</th>
                  <th className="px-6 py-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800/80 font-medium">
                {filteredPages.map((page) => (
                  <tr key={page.id} className="hover:bg-slate-800/40 transition-colors">
                    <td className="px-6 py-4 font-bold text-white">
                      <div className="flex items-center space-x-3">
                        <div className="p-2 rounded-xl bg-blue-500/10 border border-blue-500/20 text-blue-400 flex-shrink-0">
                          <FileText size={16} />
                        </div>
                        <div className="min-w-0">
                          <span className="block text-sm truncate">{page.title}</span>
                          <span className="text-[10px] text-slate-500 font-normal truncate block">{page.description || 'Standard Web Page'}</span>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 font-mono text-blue-400">/{page.slug}</td>
                    <td className="px-6 py-4">
                      <button
                        onClick={() => handleToggleStatus(page)}
                        className={`inline-flex items-center space-x-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold border cursor-pointer transition-all ${
                          page.status === 'PUBLISHED'
                            ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20 hover:bg-emerald-500/20'
                            : 'bg-amber-500/10 text-amber-400 border-amber-500/20 hover:bg-amber-500/20'
                        }`}
                        title={page.status === 'PUBLISHED' ? 'Click to unpublish' : 'Click to publish'}
                      >
                        {page.status === 'PUBLISHED' ? <ToggleRight size={12} /> : <ToggleLeft size={12} />}
                        <span>{page.status === 'PUBLISHED' ? 'Published' : 'Draft'}</span>
                      </button>
                    </td>
                    <td className="px-6 py-4">
                      <span className="inline-flex items-center space-x-1.5 px-2.5 py-1 rounded-full bg-indigo-500/10 text-indigo-400 font-bold border border-indigo-500/20">
                        <Layers size={12} />
                        <span>{page.sections?.length || 0}</span>
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      {page.seoTitle ? (
                        <span className="px-2.5 py-1 rounded-full text-[10px] font-bold bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                          ✓
                        </span>
                      ) : (
                        <span className="px-2.5 py-1 rounded-full text-[10px] font-bold bg-amber-500/10 text-amber-400 border border-amber-500/20">
                          —
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end space-x-1.5">
                        <Link
                          to={`/admin/pages/${page.id}/builder`}
                          className="inline-flex items-center space-x-1.5 px-3 py-1.5 rounded-xl font-bold text-xs bg-blue-600 hover:bg-blue-500 text-white transition-all shadow-md shadow-blue-600/20"
                        >
                          <Layers size={14} />
                          <span className="hidden xl:inline">Builder</span>
                        </Link>
                        <a
                          href={`/${page.slug === 'home' ? '' : page.slug}`}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center p-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 transition-colors"
                          title="Preview Live"
                        >
                          <Eye size={14} />
                        </a>
                        <button
                          onClick={() => handleDuplicatePage(page)}
                          className="p-2 rounded-xl bg-slate-800 hover:bg-indigo-600/20 text-slate-300 hover:text-indigo-400 transition-colors"
                          title="Duplicate Page"
                        >
                          <Copy size={14} />
                        </button>
                        <button
                          onClick={() => handleDeletePage(page)}
                          className="p-2 rounded-xl bg-slate-800 hover:bg-rose-600/20 text-slate-300 hover:text-rose-400 transition-colors"
                          title="Delete Page"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create New Page Modal */}
      {createModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-in fade-in">
          <div className="bg-slate-900 border border-slate-800 rounded-3xl w-full max-w-lg shadow-2xl overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-800 flex items-center justify-between">
              <div>
                <h2 className="text-base font-bold text-white">Create New Page</h2>
                <p className="text-xs text-slate-400">Initialize a new landing page with drag-and-drop layout builder</p>
              </div>
              <button
                onClick={() => setCreateModalOpen(false)}
                className="p-1.5 rounded-xl text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
              >
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleCreatePage} className="p-6 space-y-4 text-xs">
              <div>
                <label className="block font-semibold text-slate-300 mb-1.5">Page Title *</label>
                <input
                  type="text"
                  required
                  value={newPageData.title}
                  onChange={(e) => handleTitleChange(e.target.value)}
                  placeholder="e.g. Enterprise Cloud Security"
                  className="w-full bg-slate-950 border border-slate-800 rounded-xl px-3 py-2.5 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block font-semibold text-slate-300 mb-1.5">Slug (URL Path) *</label>
                <input
                  type="text"
                  required
                  value={newPageData.slug}
                  onChange={(e) => setNewPageData({ ...newPageData, slug: e.target.value })}
                  placeholder="e.g. enterprise-cloud-security"
                  className="w-full bg-slate-950 border border-slate-800 rounded-xl px-3 py-2.5 text-xs text-white font-mono placeholder-slate-500 focus:outline-none focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block font-semibold text-slate-300 mb-1.5">Short Description</label>
                <textarea
                  rows={2}
                  value={newPageData.description}
                  onChange={(e) => setNewPageData({ ...newPageData, description: e.target.value })}
                  placeholder="Internal summary of page purpose..."
                  className="w-full bg-slate-950 border border-slate-800 rounded-xl p-3 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-blue-500 resize-none"
                />
              </div>

              <div className="pt-4 border-t border-slate-800 flex items-center justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setCreateModalOpen(false)}
                  className="px-4 py-2 rounded-xl font-semibold text-slate-400 hover:text-white"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-5 py-2.5 rounded-xl font-bold bg-blue-600 hover:bg-blue-500 text-white shadow-lg shadow-blue-600/30 flex items-center space-x-2"
                >
                  <Check size={16} />
                  <span>Create Page</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
